/* Featured collection tabs ("New in"): WAI-ARIA tabs whose product cards load on demand.
   <featured-collection-tabs> holds a [role=tablist] of [role=tab] buttons and one [role=tabpanel] per tab.
   Each panel's data-url is "<collection url>?section_id=product-grid-cards[&sort_by=…]"
   (sections/product-grid-cards.liquid returns up to 8 grid cards).
   - The active tab's cards load when the section comes within ~600 px of the viewport (at once in the theme editor).
   - Other tabs load when opened and are prefetched on hover or focus. Responses are cached per URL.
   - Placeholder cells (one <template> cell, cloned data-count times) hold the grid's height while loading.
   - Panels the server already resolved (data-state "loaded" for an empty collection, "none" with no collection) never fetch.
   - Keyboard: Left/Right move between tabs (wrapping), Home/End go to the first/last tab.
   - Quick add inside the cards is handled by the global assets/quick-add.js (/cart/add.js). */
(() => {
  if (customElements.get('featured-collection-tabs')) return;

  /* Parsed card cells per section URL, shared across re-renders in the theme editor. */
  const cache = new Map();

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);

  const fetchDocument = async (url) => {
    if (window.LMB && typeof window.LMB.fetchHTML === 'function') return window.LMB.fetchHTML(url);
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  const requestCells = (url) => {
    if (!cache.has(url)) {
      const request = fetchDocument(url).then((doc) => {
        const list = doc.querySelector('[data-product-grid-cards]');
        if (!list) throw new Error('Product cards not found');
        return Array.from(list.children);
      });
      request.catch(() => cache.delete(url));
      cache.set(url, request);
    }
    return cache.get(url);
  };

  class FeaturedCollectionTabs extends HTMLElement {
    connectedCallback() {
      if (!this.ready) this.setup();
      if (this.tabs.length) this.loadWhenNear();
    }

    disconnectedCallback() {
      this.stopObserving();
    }

    setup() {
      this.ready = true;
      this.tablist = this.querySelector('[role="tablist"]');
      this.tabs = this.tablist ? Array.from(this.tablist.querySelectorAll('[role="tab"]')) : [];
      this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
      this.skeleton = this.querySelector('template[data-fc-skeleton]');
      if (!this.tabs.length) return;

      this.tablist.addEventListener('click', (event) => {
        const index = this.tabs.indexOf(event.target.closest('[role="tab"]'));
        if (index > -1) this.select(index);
      });
      this.tablist.addEventListener('keydown', (event) => this.onKeydown(event));
      this.tabs.forEach((tab, index) => {
        const prefetch = () => this.prefetch(index);
        tab.addEventListener('pointerenter', prefetch);
        tab.addEventListener('focus', prefetch);
      });
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.tabs.indexOf(event.target.closest && event.target.closest('[role="tab"]'));
        if (index > -1) this.select(index);
      });
    }

    get activeIndex() {
      return Math.max(
        0,
        this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
      );
    }

    loadWhenNear() {
      this.stopObserving();
      const panel = this.panels[this.activeIndex];
      if (!panel || panel.dataset.state) return;
      if (designMode() || !('IntersectionObserver' in window)) {
        this.load(this.activeIndex);
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.stopObserving();
          this.load(this.activeIndex);
        },
        { rootMargin: '600px 0px' }
      );
      this.observer.observe(this);
    }

    stopObserving() {
      if (this.observer) this.observer.disconnect();
      this.observer = null;
    }

    select(index, { focus = false } = {}) {
      const tab = this.tabs[index];
      if (!tab) return;
      this.tabs.forEach((item, i) => {
        const active = i === index;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
        if (this.panels[i]) this.panels[i].hidden = !active;
      });
      /* No native scroll-into-view: reveal() scrolls only the tab row, smoothly unless motion is reduced. */
      if (focus) tab.focus({ preventScroll: true });
      this.reveal(tab);
      this.stopObserving();
      this.load(index);
    }

    onKeydown(event) {
      const index = this.tabs.indexOf(event.target.closest('[role="tab"]'));
      /* Leave browser shortcuts such as Alt+Left (back) alone. */
      if (index < 0 || event.altKey || event.ctrlKey || event.metaKey) return;
      const last = this.tabs.length - 1;
      let next;
      switch (event.key) {
        case 'ArrowRight':
          next = index === last ? 0 : index + 1;
          break;
        case 'ArrowLeft':
          next = index === 0 ? last : index - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return;
      }
      event.preventDefault();
      this.select(next, { focus: true });
    }

    /* Phones: keep the chosen tab visible in the scrolling row without scrolling the page. */
    reveal(tab) {
      const list = this.tablist;
      if (list.scrollWidth <= list.clientWidth + 1) return;
      const left = tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
      list.scrollTo({ left: Math.max(0, left), behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    prefetch(index) {
      const panel = this.panels[index];
      if (panel && panel.dataset.url && !panel.dataset.state) requestCells(panel.dataset.url).catch(() => {});
    }

    async load(index) {
      const panel = this.panels[index];
      if (!panel || !panel.dataset.url) return;
      const state = panel.dataset.state;
      if (state === 'loading' || state === 'loaded') return;
      const grid = panel.querySelector('[data-fc-grid]');
      if (!grid) return;

      panel.querySelectorAll('[data-fc-message]').forEach((message) => message.remove());
      grid.hidden = false;
      if (!grid.children.length && this.skeleton) {
        /* As many placeholders as the collection has products (up to the section's 8 or 4). */
        const count = Math.max(1, Math.min(Number(panel.dataset.count) || 8, 8));
        for (let i = 0; i < count; i += 1) grid.append(this.skeleton.content.cloneNode(true));
      }
      panel.dataset.state = 'loading';
      panel.setAttribute('aria-busy', 'true');

      try {
        const cells = await requestCells(panel.dataset.url);
        if (cells.length) {
          grid.replaceChildren(...cells.map((cell) => this.adopt(cell, panel.id)));
        } else {
          grid.replaceChildren();
          grid.hidden = true;
          this.message(panel, this.dataset.emptyText);
        }
        panel.dataset.state = 'loaded';
      } catch (error) {
        grid.replaceChildren();
        grid.hidden = true;
        this.message(panel, this.dataset.errorText, panel.dataset.collectionUrl);
        /* Opening the tab again retries. */
        panel.dataset.state = 'error';
      } finally {
        panel.removeAttribute('aria-busy');
      }
    }

    /* Copies a fetched cell into the page, making its ids unique (the same product can appear in several tabs). */
    adopt(cell, prefix) {
      const node = document.importNode(cell, true);
      node.querySelectorAll('[id]').forEach((element) => {
        const oldId = element.id;
        const newId = `${prefix}-${oldId}`;
        element.id = newId;
        node.querySelectorAll('[aria-controls]').forEach((control) => {
          if (control.getAttribute('aria-controls') === oldId) control.setAttribute('aria-controls', newId);
        });
      });
      return node;
    }

    message(panel, text, url) {
      if (!text) return;
      const message = document.createElement('p');
      message.className = 'fc__message';
      message.dataset.fcMessage = '';
      message.textContent = text;
      if (url && this.dataset.viewCollectionText) {
        const link = document.createElement('a');
        const arrow = document.createElement('span');
        link.href = url;
        link.textContent = this.dataset.viewCollectionText;
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = ' →';
        link.append(arrow);
        message.append(' ', link);
      }
      panel.append(message);
    }
  }

  customElements.define('featured-collection-tabs', FeaturedCollectionTabs);
})();
